/**
 * Testimonial carousel — prev/next controls + mouse/touch drag
 */
class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tc-track]');
    this.prevBtn = this.querySelector('[data-tc-prev]');
    this.nextBtn = this.querySelector('[data-tc-next]');
    if (!this.track) return;

    this.isDragging = false;
    this.hasDragged = false;
    this.dragAxis = null;
    this.startX = 0;
    this.startY = 0;
    this.scrollStart = 0;
    this.pointerId = null;

    this.onScroll = () => this.updateButtons();
    this.onPointerDown = (event) => this.handlePointerDown(event);
    this.onPointerMove = (event) => this.handlePointerMove(event);
    this.onPointerUp = (event) => this.handlePointerUp(event);
    this.onClickCapture = (event) => this.handleClickCapture(event);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    this.track.addEventListener('click', this.onClickCapture, true);
    this.prevBtn?.addEventListener('click', () => this.scrollBy(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollBy(1));
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.updateButtons();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('click', this.onClickCapture, true);
    window.removeEventListener('resize', this.onScroll);
  }

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('a, button, input, textarea, select, label')) return;
    // Mobile stacks cards (no horizontal track) — never capture touches.
    if (event.pointerType === 'touch' || window.matchMedia('(max-width: 749px)').matches) return;

    // Do not capture yet — wait until the gesture is clearly horizontal so vertical
    // page scrolling near this section (e.g. Mission → Testimonials) isn't blocked.
    this.isDragging = true;
    this.hasDragged = false;
    this.dragAxis = null;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.scrollStart = this.track.scrollLeft;
  }

  handlePointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (this.dragAxis == null) {
      if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;

      // Vertical intent → abandon drag and let the page scroll normally
      if (Math.abs(deltaY) >= Math.abs(deltaX)) {
        this.isDragging = false;
        this.pointerId = null;
        this.dragAxis = null;
        return;
      }

      this.dragAxis = 'x';
      this.track.classList.add('is-dragging');
      this.track.style.scrollBehavior = 'auto';
      this.track.style.scrollSnapType = 'none';

      try {
        this.track.setPointerCapture(event.pointerId);
      } catch (_error) {
        // Ignore browsers that reject capture on this target
      }
    }

    if (this.dragAxis !== 'x') return;

    if (Math.abs(deltaX) > 3) {
      this.hasDragged = true;
    }

    this.track.scrollLeft = this.scrollStart - deltaX;
    event.preventDefault();
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.pointerId) return;

    const wasHorizontalDrag = this.dragAxis === 'x';

    this.isDragging = false;
    this.pointerId = null;
    this.dragAxis = null;
    this.track.classList.remove('is-dragging');
    this.track.style.scrollBehavior = '';
    this.track.style.scrollSnapType = '';

    try {
      if (wasHorizontalDrag && this.track.hasPointerCapture?.(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
    } catch (_error) {
      // Ignore release errors
    }

    this.updateButtons();

    // Keep suppressing accidental clicks briefly after a drag
    if (this.hasDragged) {
      window.setTimeout(() => {
        this.hasDragged = false;
      }, 50);
    }
  }

  handleClickCapture(event) {
    if (!this.hasDragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.hasDragged = false;
  }

  getSlideStep() {
    const slide = this.track.querySelector('[data-tc-slide]');
    if (!slide) return this.track.clientWidth * 0.8;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  scrollBy(direction) {
    this.track.scrollBy({ left: direction * this.getSlideStep(), behavior: 'smooth' });
  }

  updateButtons() {
    if (!this.track) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
    const left = this.track.scrollLeft;
    if (this.prevBtn) this.prevBtn.disabled = left <= 2;
    if (this.nextBtn) this.nextBtn.disabled = left >= maxScroll;
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
